/**
 * Reading module data transformation utilities.
 *
 * Transforms backend SimulationData structure to frontend TextChunk and Question types.
 *
 * @module readingDataTransform
 */

import { transformQuestion } from './questionTransform';
import type {
  SimulationData,
  SimulationSection,
  QuestionGroup,
  TextChunk,
  Question,
  Paragraph,
} from '@/types/exam-simulation';

/**
 * Split text content into paragraphs by separator (---)
 *
 * Note: Does NOT split on "------" (6 dashes) as that's used for two-column layout.
 *
 * @param content - Text content that may contain paragraph separators
 * @returns Array of paragraph objects with IDs
 */
function splitIntoParagraphs(content: string): Paragraph[] {
  // First check if content contains "------" separator (used for two-column layout)
  // If so, don't split it - keep it as one paragraph
  if (content.includes('------')) {
    return [{ id: 1, content: content.trim() || '' }];
  }

  // Split by "---" (3 dashes) but NOT "------" (6 dashes)
  // Use regex that matches exactly 3-5 dashes (not 6+)
  const sections = content.split(/^-{3,5}$/m).filter((section) => section.trim());

  if (sections.length === 0) {
    // Preserve original content, only trim leading/trailing whitespace
    // Using trim() removes leading/trailing whitespace but preserves internal newlines
    return [{ id: 1, content: content.trim() || '' }];
  }

  return sections.map((section, index) => {
    // Trim leading/trailing whitespace, but preserve internal newlines and formatting
    // The whitespace-pre-line CSS class will handle displaying newlines correctly
    return {
      id: index + 1,
      content: section.trim(),
    };
  });
}


/**
 * Transform question group to questions with paragraph reference
 *
 * @param group - Question group from backend
 * @param startQuestionId - Starting question ID for numbering
 * @param startParagraphId - Starting global paragraph ID for this group's context
 * @returns Array of transformed questions
 */
function transformQuestionGroup(
  group: QuestionGroup,
  startQuestionId: number,
  startParagraphId: number,
  section?: number,
): Question[] {
  if (!group.context) {
    // If no context, use paragraph 1 as default
    return group.questions.map((q, index) =>
      transformQuestion(q, startQuestionId + index, 1, section),
    );
  }

  // Split context content into paragraphs
  const paragraphs = splitIntoParagraphs(group.context.content);

  // Map questions to paragraphs using global paragraph IDs
  // All questions from this group should reference paragraphs from this group's context
  const questions: Question[] = group.questions.map((q, index) => {
    let paragraphRef = startParagraphId; // Default to first paragraph of this group's context
    if (paragraphs.length > 1) {
      // Distribute questions across paragraphs within this group's context
      // Calculate local paragraph index (0-based), then add to startParagraphId
      const localParagraphIndex = Math.min(
        Math.floor((index / group.questions.length) * paragraphs.length),
        paragraphs.length - 1,
      );
      paragraphRef = startParagraphId + localParagraphIndex;
    }
    return transformQuestion(q, startQuestionId + index, paragraphRef, section);
  });

  return questions;
}

/**
 * Transform simulation section to text chunk
 *
 * @param section - Backend simulation section
 * @param startQuestionId - Starting question ID for numbering
 * @returns Frontend text chunk object
 */
function transformSection(
  section: SimulationSection,
  startQuestionId: number,
): { chunk: TextChunk; questions: Question[]; nextQuestionId: number } {
  // Collect all questions from all question groups in this section
  const allQuestions: Question[] = [];
  let currentQuestionId = startQuestionId;

  // Collect all paragraphs from all contexts in this section
  const allParagraphs: Paragraph[] = [];
  let paragraphIdCounter = 1;

  // Process each question group
  for (const group of section.questionGroups) {
    // Record the starting paragraph ID for this group before processing
    const groupStartParagraphId = paragraphIdCounter;

    if (group.context) {
      // Split context content into paragraphs
      const paragraphs = splitIntoParagraphs(group.context.content);
      // Add paragraphs with sequential IDs
      paragraphs.forEach((para) => {
        allParagraphs.push({
          id: paragraphIdCounter++,
          content: para.content,
        });
      });
    }

    // Transform questions for this group, passing the starting paragraph ID
    // If no context, startParagraphId will be the current counter (which may be > 1)
    // but questions without context default to paragraph 1 anyway
    const groupQuestions = transformQuestionGroup(
      group,
      currentQuestionId,
      groupStartParagraphId,
      section.section,
    );
    allQuestions.push(...groupQuestions);
    currentQuestionId += groupQuestions.length;
  }

  // If no paragraphs found, create a placeholder
  if (allParagraphs.length === 0) {
    allParagraphs.push({
      id: 1,
      content: '',
    });
  }

  // Create text chunk
  const chunk: TextChunk = {
    id: section.section,
    title: section.title || `Passage ${section.section}`,
    paragraphs: allParagraphs,
    questionIds: allQuestions.map((q) => q.id),
  };

  return {
    chunk,
    questions: allQuestions,
    nextQuestionId: currentQuestionId,
  };
}

/**
 * Transform backend simulation data to frontend format.
 *
 * Converts the backend SimulationData structure (with sections, question groups,
 * and contexts) into the frontend format with TextChunks and Questions. Handles
 * paragraph splitting, question numbering, and paragraph-question associations.
 *
 * @param data - Backend simulation data containing sections with question groups and contexts
 * @returns Object containing:
 *   - `chunks`: Array of TextChunk objects (one per section) with paragraphs and question IDs
 *   - `allQuestions`: Array of all Question objects with sequential IDs and paragraph references
 *
 * @example
 * ```typescript
 * const { chunks, allQuestions } = transformSimulationData(simulationData);
 * // chunks[0] contains paragraphs and questionIds for section 1
 * // allQuestions contains all questions with sequential IDs
 * ```
 */
export function transformSimulationData(data: SimulationData): {
  chunks: TextChunk[];
  allQuestions: Question[];
} {
  const chunks: TextChunk[] = [];
  const allQuestions: Question[] = [];
  let currentQuestionId = 1;

  // Transform each section
  for (const section of data.sections) {
    const { chunk, questions, nextQuestionId } = transformSection(
      section,
      currentQuestionId,
    );
    chunks.push(chunk);
    allQuestions.push(...questions);
    currentQuestionId = nextQuestionId;
  }

  return { chunks, allQuestions };
}

